import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { toast } from 'sonner'

import { CacheKeys } from '@/lib/cache'
import { toCamelCase, toSnakeCase } from '@/lib/typeHelper'
import type { InsuranceApplication } from '@/models/insuranceApplication'
import type { InsuranceIndication } from '@/models/insuranceIndication'
import type {
  serializeCoverages,
  serializeFees,
} from '@/routes/Private/App/Customers/Insurance/insuranceFormShared'

/** What we read off an uploaded quote, for the customer to check before it's added. */
export interface ParsedCustomerQuote {
  documentId: string
  carrierName: string | null
  coverages: {
    coverageType: string
    premium: number | null
    limit: number | null
    deductible: number | null
    comprehensiveDeductible?: number | null
  }[]
  oneTimeFees: { label: string; amount: number }[]
  warnings: string[]
}

/**
 * Reads a quote the customer got from another broker, without adding it yet. It goes
 * on the board next to real carrier prices, so they get to check what we read first.
 */
function parseCustomerQuote(orgId: string, file: File): Promise<ParsedCustomerQuote> {
  const formData = new FormData()
  formData.append('file', file)
  return axios
    .post(`/api/v1/insure/organisations/${orgId}/customer-quotes/parse/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => toCamelCase(res.data) as ParsedCustomerQuote)
}

export interface ConfirmCustomerQuoteInput {
  documentId: string
  /** The reviewed coverages and fees — what the customer corrected, not what we read. */
  coverages: ReturnType<typeof serializeCoverages>
  oneTimeFees: ReturnType<typeof serializeFees>
}

/** Adds the quote the customer just reviewed, with any corrections they made. */
function confirmCustomerQuote(orgId: string, input: ConfirmCustomerQuoteInput): Promise<InsuranceIndication> {
  return axios
    .post(`/api/v1/insure/organisations/${orgId}/customer-quotes/`, toSnakeCase(input))
    .then((res) => toCamelCase(res.data) as InsuranceIndication)
}

function deleteCustomerQuote(orgId: string, indicationId: string): Promise<void> {
  return axios.delete(`/api/v1/insure/organisations/${orgId}/customer-quotes/${indicationId}/`)
}

/** The application payload carries the indications, so that's what has to be refetched. */
function useInvalidateApplication() {
  const queryClient = useQueryClient()
  // Returned so react-query awaits it: the mutation stays pending until the board holds the change.
  return () => queryClient.invalidateQueries({ queryKey: [CacheKeys.APPLICATION] })
}

export function useParseCustomerQuote(orgId: string) {
  return useMutation({ mutationFn: (file: File) => parseCustomerQuote(orgId, file) })
}

export function useConfirmCustomerQuote(orgId: string) {
  const invalidate = useInvalidateApplication()
  return useMutation({
    mutationFn: (input: ConfirmCustomerQuoteInput) => confirmCustomerQuote(orgId, input),
    onSuccess: invalidate,
  })
}

/**
 * Takes the card off the board on click, not when the server answers. The delete returns
 * in ~300ms, but the card lives in the application payload, which takes ~2s to rebuild —
 * so the button used to sit silent long enough to read as broken.
 */
export function useDeleteCustomerQuote(orgId: string) {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateApplication()
  return useMutation({
    mutationFn: (indicationId: string) => deleteCustomerQuote(orgId, indicationId),
    onMutate: async (indicationId: string) => {
      // A poll already in flight answers from before the delete and would re-add the card.
      await queryClient.cancelQueries({ queryKey: [CacheKeys.APPLICATION] })
      const removed: { key: QueryKey; index: number; indication: InsuranceIndication }[] = []
      for (const [key, application] of queryClient.getQueriesData<InsuranceApplication>({
        queryKey: [CacheKeys.APPLICATION],
      })) {
        const index = application?.indications.findIndex(({ id }) => id === indicationId) ?? -1
        if (!application || index < 0) continue
        removed.push({ key, index, indication: application.indications[index] })
        queryClient.setQueryData(key, {
          ...application,
          indications: application.indications.filter(({ id }) => id !== indicationId),
        })
      }
      return { removed }
    },
    onError: (_error, _indicationId, context) => {
      // Only this card: a whole-snapshot restore would resurrect one a later delete removed.
      for (const { key, index, indication } of context?.removed ?? []) {
        queryClient.setQueryData<InsuranceApplication>(key, (current) => {
          if (!current?.indications) return current
          // A poll answered after the click already put it back; splicing would double it.
          if (current.indications.some(({ id }) => id === indication.id)) return current
          const indications = [...current.indications]
          indications.splice(index, 0, indication)
          return { ...current, indications }
        })
      }
      toast.error("We couldn't remove that quote. Please try again.")
    },
    // On settle, not on success: a rollback also has to be reconciled with the server.
    onSettled: invalidate,
  })
}
